import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core'
import { trigger, state, style, transition, animate } from '@angular/animations'
import { Subject, Subscription, interval } from 'rxjs'
import { finalize, takeUntil } from 'rxjs/operators'
import { UiService } from 'src/app/services/ui.service'
import { StickyOffsetService } from '../services/sticky-offset.service'
import { StacksetsRegenerationPollService } from '../services/stacksets-regeneration-poll.service'
import { CopyFeedbackService } from 'src/app/shared/copy-feedback.service'
import {
  StacksetsConfig,
  StacksetsConfigCacheService,
  StacksetsManagementTopology,
  createDefaultStacksetsConfig,
} from '../services/stacksets-config-cache.service'

@Component({
  selector: 'stacksets-instructions',
  templateUrl: './stacksets-instructions.component.html',
  styleUrls: ['./stacksets-instructions.component.scss'],
  standalone: false,
  providers: [CopyFeedbackService],
  animations: [
    trigger('slideDown', [
      state('collapsed', style({ height: '0', overflow: 'hidden' })),
      state('expanded', style({ height: '*', overflow: 'visible' })),
      transition('collapsed <=> expanded', [animate('200ms ease-in-out')]),
    ]),
  ],
})
export class StacksetsInstructionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() sourceStackSetTemplateUrl: string
  @Input() targetStackSetTemplateUrl: string
  @Input() sourceTemplateBucket: string
  @Input() sourceTemplateKey: string
  @Input() targetTemplateBucket: string
  @Input() targetTemplateKey: string
  @Input() sourceAccountId: string
  @Input() sourceRegion: string
  @Input() targetAccountId: string
  @Input() targetRegion: string

  // Access state inputs - to determine if onboarding is needed
  @Input() sourceStackName: string
  @Input() targetStackName: string

  // Whether the stacks are managed by StackSet (from backend access check)
  @Input() sourceManagedByStackSet: boolean = false
  @Input() targetManagedByStackSet: boolean = false

  // Management account ID (if known, passed from parent)
  @Input() managementAccountId: string = ''

  // Reference element ID for sticky offset calculation
  @Input() stickyReferenceId: string = ''

  // Testing state - true when access check is in progress
  @Input() testingSource: boolean = false
  @Input() testingTarget: boolean = false

  // Error state - true when access check failed with an error
  @Input() sourceError: boolean = false
  @Input() targetError: boolean = false

  // Completed test counts for retry messaging
  @Input() sourceTestCount: number = 0
  @Input() targetTestCount: number = 0

  // Migration mode - when true, adjusts messaging for CF → StackSets migration
  // (existing stacks are expected, cleanup guidance shown after success)
  @Input() migrationMode: boolean = false

  // Hide the component's h2/intro when parent provides its own header
  @Input() hideHeader: boolean = false

  // Arpio account ID and regeneration job ID — both required to poll. Step 3 is
  // gated by regenerationInProgress while the backend regenerates other sync pairs;
  // unblocks on any terminal status or timeout (the requested pair's templates
  // are already valid even if broader regen fails).
  @Input() arpioAccountId: string = ''
  @Input() regenerationJobId: string | null = null

  // Emitted when user clicks "Test Access" button - emits 'source' or 'target'
  @Output() testAccess = new EventEmitter<'source' | 'target'>()

  // Emitted when user clicks "Finish Set Up" after migration completes
  @Output() finishSetup = new EventEmitter<void>()

  // Expanded sections (all closed by default)
  expandedSections: Set<string> = new Set()

  // Sticky offset for step headers
  stickyOffset: number = 0
  private destroy$ = new Subject<void>()

  // Polling for step 3 (deploy section)
  private pollingSubscription: Subscription | null = null
  private readonly POLLING_INTERVAL_MS = 10000

  // Regeneration job poll — gates Step 3 until backend finishes regenerating other sync pairs.
  // Terminal status / failure / timeout all unblock; feedback toasts live in the shared service.
  regenerationInProgress = false
  private regenerationPollSub: Subscription | null = null

  // User-entered StackSets values, backed by the per-Arpio-account localStorage cache.
  // The cache pre-fills these on ngOnInit and every mutation here mirrors back to it.
  config: StacksetsConfig = createDefaultStacksetsConfig()

  constructor(
    public uiService: UiService,
    public copyFeedback: CopyFeedbackService,
    private stickyOffsetService: StickyOffsetService,
    private regenerationPollService: StacksetsRegenerationPollService,
    private configCache: StacksetsConfigCacheService
  ) {}

  // Get unique account IDs (avoids showing duplicates when source and target are same account)
  get uniqueAccountIds(): string[] {
    const accounts = new Set<string>()
    if (this.sourceAccountId) accounts.add(this.sourceAccountId)
    if (this.targetAccountId) accounts.add(this.targetAccountId)
    return Array.from(accounts)
  }

  // Per-field accessors keep the template's `[(ngModel)]` bindings clean and let each
  // write fan out to `afterConfigChange()`. They all project into `this.config`.
  get managementTopology(): StacksetsManagementTopology {
    return this.config.managementTopology
  }
  set managementTopology(value: StacksetsManagementTopology) {
    this.config.managementTopology = value
    this.afterConfigChange()
  }

  get primaryManagementAccountId(): string {
    return this.config.primaryManagementAccountId
  }
  set primaryManagementAccountId(value: string) {
    this.config.primaryManagementAccountId = value
    this.afterConfigChange()
  }

  get recoveryManagementAccountId(): string {
    return this.config.recoveryManagementAccountId
  }
  set recoveryManagementAccountId(value: string) {
    this.config.recoveryManagementAccountId = value
    this.afterConfigChange()
  }

  get primaryStackSetName(): string {
    return this.config.primaryStackSetName
  }
  set primaryStackSetName(value: string) {
    this.config.primaryStackSetName = value
    this.afterConfigChange()
  }

  get recoveryStackSetName(): string {
    return this.config.recoveryStackSetName
  }
  set recoveryStackSetName(value: string) {
    this.config.recoveryStackSetName = value
    this.afterConfigChange()
  }

  private afterConfigChange(): void {
    this.configCache.save(this.arpioAccountId, this.config)
  }

  get isTwoMgmtAccounts(): boolean {
    return this.managementTopology === 'two'
  }

  onSingleMgmtToggle(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked
    this.managementTopology = checked ? 'one' : 'two'
  }

  ngOnInit(): void {
    // Step 1 (Configuration) auto-expands so users land directly on the inputs that
    // drive every command below; the remaining steps stay collapsed until clicked.
    this.setSectionExpanded('config', true)

    // Pre-fill config from the per-Arpio-account cache (anything the user entered
    // previously for this account, in either setup or update flow).
    const cached = this.configCache.load(this.arpioAccountId)
    if (cached) this.config = cached

    // Subscribe to sticky offset changes if a reference element is provided
    if (this.stickyReferenceId) {
      this.stickyOffsetService
        .getOffset$(this.stickyReferenceId)
        .pipe(takeUntil(this.destroy$))
        .subscribe((offset) => {
          this.stickyOffset = offset
        })
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['regenerationJobId'] || changes['arpioAccountId']) {
      this.refreshRegenerationPoll()
    }
  }

  ngOnDestroy(): void {
    this.stopPolling()
    this.stopRegenerationPoll()
    this.destroy$.next()
    this.destroy$.complete()
  }

  // Single mutation point for expandedSections. Any side effect tied to a section
  // being open/closed (e.g. the deploy-step polling lifecycle) lives here so every
  // entry point — header toggle, CONTINUE button, in-page "go to Step N" links —
  // gets the same behavior for free.
  private setSectionExpanded(section: string, expanded: boolean): void {
    const was = this.expandedSections.has(section)
    if (expanded === was) return
    if (expanded) this.expandedSections.add(section)
    else this.expandedSections.delete(section)
    if (section === 'deploy') {
      expanded ? this.startPolling() : this.stopPolling()
    }
  }

  // Section toggle
  toggleSection(section: string): void {
    this.setSectionExpanded(section, !this.expandedSections.has(section))
  }

  // Continue button: collapses the current step, then opens-and-scrolls to the next.
  // (scrollToStep expands its target via setSectionExpanded.) Mirrors the pattern in
  // the launch-recovery dialog.
  continueToStep(currentKey: string, nextKey: string): void {
    this.setSectionExpanded(currentKey, false)
    this.scrollToStep(nextKey)
  }

  private startPolling(): void {
    this.stopPolling()
    this.pollingSubscription = interval(this.POLLING_INTERVAL_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Skip polling for endpoints with errors or already being tested
        if (this.needsSourceOnboarding && !this.testingSource && !this.sourceError) {
          this.testAccess.emit('source')
        }
        if (this.needsTargetOnboarding && !this.testingTarget && !this.targetError) {
          this.testAccess.emit('target')
        }
      })
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe()
      this.pollingSubscription = null
    }
  }

  // Re-evaluates the polling lifecycle when inputs change. With no jobId we leave
  // regenerationInProgress=false (regular templates / cache-hit paths skip the gate).
  private refreshRegenerationPoll(): void {
    this.stopRegenerationPoll()

    if (!this.regenerationJobId || !this.arpioAccountId) {
      this.regenerationInProgress = false
      return
    }

    this.regenerationInProgress = true

    this.regenerationPollSub = this.regenerationPollService
      .pollUntilDone(this.arpioAccountId, this.regenerationJobId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.regenerationInProgress = false
          this.regenerationPollSub = null
        })
      )
      .subscribe()
  }

  private stopRegenerationPoll(): void {
    if (this.regenerationPollSub) {
      this.regenerationPollSub.unsubscribe()
      this.regenerationPollSub = null
    }
  }

  isExpanded(section: string): boolean {
    return this.expandedSections.has(section)
  }

  onCopied(commandId: string): void {
    this.copyFeedback.onCopied(commandId)
  }

  isCopied(commandId: string): boolean {
    return !!this.copyFeedback.copiedStates[commandId]
  }

  // Download command as shell script
  downloadCommand(commandId: string, command: string): void {
    const content = `#!/bin/bash\n\n${command}\n`
    const blob = new Blob([content], { type: 'text/x-sh' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `arpio-${commandId}.sh`
    a.click()
    URL.revokeObjectURL(url)
  }

  get needsSourceOnboarding(): boolean {
    // In StackSets flow, we check if access is managed by StackSet, not just if any stack exists
    return !this.sourceManagedByStackSet
  }

  get needsTargetOnboarding(): boolean {
    // In StackSets flow, we check if access is managed by StackSet, not just if any stack exists
    return !this.targetManagedByStackSet
  }

  // Check if access exists via CloudFormation but NOT via StackSets
  // Used to show informational message about existing CF stacks
  get hasSourceCfnButNotStackSet(): boolean {
    return !!this.sourceStackName && !this.sourceManagedByStackSet
  }

  get hasTargetCfnButNotStackSet(): boolean {
    return !!this.targetStackName && !this.targetManagedByStackSet
  }

  // Migration complete - both accounts now managed by StackSets
  get migrationComplete(): boolean {
    return this.migrationMode && this.sourceManagedByStackSet && this.targetManagedByStackSet
  }

  // Commands for cleaning up old CloudFormation stacks after migration
  get deleteSourceCfnStackCommand(): string {
    if (!this.sourceStackName) return ''
    return `aws cloudformation delete-stack \\
  --stack-name ${this.sourceStackName} \\
  --region ${this.sourceRegion}`
  }

  get deleteTargetCfnStackCommand(): string {
    if (!this.targetStackName) return ''
    return `aws cloudformation delete-stack \\
  --stack-name ${this.targetStackName} \\
  --region ${this.targetRegion}`
  }

  onTestAccess(scope: 'source' | 'target'): void {
    this.testAccess.emit(scope)
  }

  // RECHECK on the CF-conflict banner — re-runs access check for whichever side(s) still
  // have a CloudFormation stack so the banner clears once the user has removed them.
  onRecheckAccess(): void {
    if (this.hasSourceCfnButNotStackSet) this.testAccess.emit('source')
    if (this.hasTargetCfnButNotStackSet) this.testAccess.emit('target')
  }

  get sourceRetryText(): string {
    const textOptions = ["It's still not working.", "Hmm... It's not working yet."]
    return this.sourceTestCount ? textOptions[this.sourceTestCount % textOptions.length] : ''
  }

  get targetRetryText(): string {
    const textOptions = ["It's still not working.", "Hmm... It's not working yet."]
    return this.targetTestCount ? textOptions[this.targetTestCount % textOptions.length] : ''
  }

  // Resolved management account placeholders. In single-mgmt topology both Primary and
  // Recovery commands resolve to the same account; in two-mgmt topology each side resolves
  // to its own. The `managementAccountId` @Input is a pre-fill hint from the parent and is
  // only used when the user hasn't typed anything yet.
  get primaryMgmtAccountPlaceholder(): string {
    return this.primaryManagementAccountId || this.managementAccountId || '<YOUR-MANAGEMENT-ACCOUNT-ID>'
  }

  get recoveryMgmtAccountPlaceholder(): string {
    if (this.isTwoMgmtAccounts) {
      return this.recoveryManagementAccountId || '<YOUR-RECOVERY-MANAGEMENT-ACCOUNT-ID>'
    }
    return this.primaryMgmtAccountPlaceholder
  }

  // Helpers for StackSet name placeholders
  get primaryStackSetNameValue(): string {
    return this.primaryStackSetName || '<YOUR-PRIMARY-STACKSET-NAME>'
  }

  get recoveryStackSetNameValue(): string {
    return this.recoveryStackSetName || '<YOUR-RECOVERY-STACKSET-NAME>'
  }

  // ==================== COMMAND GENERATORS ====================

  get adminRoleCommand(): string {
    return `aws cloudformation create-stack \\
  --stack-name ArpioStackSetAdministrationRole \\
  --capabilities CAPABILITY_NAMED_IAM \\
  --template-body "$(cat <<'EOF'
AWSTemplateFormatVersion: '2010-09-09'
Description: StackSet Administration Role for Arpio
Parameters:
  ExecutionRoleName:
    Type: String
    Default: ArpioStackSetExecutionRole
Resources:
  AdministrationRole:
    Type: AWS::IAM::Role
    Properties:
      RoleName: ArpioStackSetAdministrationRole
      AssumeRolePolicyDocument:
        Version: '2012-10-17'
        Statement:
          - Effect: Allow
            Principal:
              Service: cloudformation.amazonaws.com
            Action: sts:AssumeRole
      Policies:
        - PolicyName: AssumeExecutionRole
          PolicyDocument:
            Version: '2012-10-17'
            Statement:
              - Effect: Allow
                Action: sts:AssumeRole
                Resource: !Sub 'arn:aws:iam::*:role/\${ExecutionRoleName}'
EOF
)"`
  }

  get executionRoleCommand(): string {
    return this.buildExecutionRoleCommand(this.primaryMgmtAccountPlaceholder)
  }

  // Per-member-account exec role command. In two-mgmt topology each member account trusts a
  // different management account (primary member → primary mgmt; recovery member → recovery mgmt).
  get executionRoleCommandPrimary(): string {
    return this.buildExecutionRoleCommand(this.primaryMgmtAccountPlaceholder)
  }

  get executionRoleCommandRecovery(): string {
    return this.buildExecutionRoleCommand(this.recoveryMgmtAccountPlaceholder)
  }

  private buildExecutionRoleCommand(administrationAccountId: string): string {
    return `aws cloudformation create-stack \\
  --stack-name ArpioStackSetExecutionRole \\
  --capabilities CAPABILITY_NAMED_IAM \\
  --parameters ParameterKey=AdministrationAccountId,ParameterValue=${administrationAccountId} \\
  --template-body "$(cat <<'EOF'
AWSTemplateFormatVersion: '2010-09-09'
Description: StackSet Execution Role for Arpio
Parameters:
  AdministrationAccountId:
    Type: String
    Description: AWS Account ID of the Administration Account
  AdministrationRoleName:
    Type: String
    Default: ArpioStackSetAdministrationRole
Resources:
  ExecutionRole:
    Type: AWS::IAM::Role
    Properties:
      RoleName: ArpioStackSetExecutionRole
      AssumeRolePolicyDocument:
        Version: '2012-10-17'
        Statement:
          - Effect: Allow
            Principal:
              AWS: !Sub 'arn:aws:iam::\${AdministrationAccountId}:role/\${AdministrationRoleName}'
            Action: sts:AssumeRole
      ManagedPolicyArns:
        - arn:aws:iam::aws:policy/AdministratorAccess
EOF
)"`
  }

  get createPrimaryStackSetCommand(): string {
    return `aws cloudformation create-stack-set \\
  --stack-set-name ${this.primaryStackSetNameValue} \\
  --template-url ${this.sourceStackSetTemplateUrl} \\
  --permission-model SELF_MANAGED \\
  --administration-role-arn arn:aws:iam::${this.primaryMgmtAccountPlaceholder}:role/ArpioStackSetAdministrationRole \\
  --execution-role-name ArpioStackSetExecutionRole \\
  --capabilities CAPABILITY_NAMED_IAM`
  }

  get createRecoveryStackSetCommand(): string {
    return `aws cloudformation create-stack-set \\
  --stack-set-name ${this.recoveryStackSetNameValue} \\
  --template-url ${this.targetStackSetTemplateUrl} \\
  --permission-model SELF_MANAGED \\
  --administration-role-arn arn:aws:iam::${this.recoveryMgmtAccountPlaceholder}:role/ArpioStackSetAdministrationRole \\
  --execution-role-name ArpioStackSetExecutionRole \\
  --capabilities CAPABILITY_NAMED_IAM`
  }

  get deployPrimaryCommand(): string {
    return `aws cloudformation create-stack-instances \\
  --stack-set-name ${this.primaryStackSetNameValue} \\
  --accounts ${this.sourceAccountId} \\
  --regions ${this.sourceRegion} \\
  --parameter-overrides \\
    ParameterKey=SourceAwsAccountId,ParameterValue=${this.sourceAccountId} \\
    ParameterKey=SourceRegion,ParameterValue=${this.sourceRegion} \\
    ParameterKey=TargetAwsAccountId,ParameterValue=${this.targetAccountId} \\
    ParameterKey=TargetRegion,ParameterValue=${this.targetRegion} \\
    ParameterKey=TemplateS3Bucket,ParameterValue=${this.sourceTemplateBucket} \\
    ParameterKey=TemplateS3Key,ParameterValue=${this.sourceTemplateKey}`
  }

  get deployRecoveryCommand(): string {
    return `aws cloudformation create-stack-instances \\
  --stack-set-name ${this.recoveryStackSetNameValue} \\
  --accounts ${this.targetAccountId} \\
  --regions ${this.targetRegion} \\
  --parameter-overrides \\
    ParameterKey=SourceAwsAccountId,ParameterValue=${this.sourceAccountId} \\
    ParameterKey=SourceRegion,ParameterValue=${this.sourceRegion} \\
    ParameterKey=TargetAwsAccountId,ParameterValue=${this.targetAccountId} \\
    ParameterKey=TargetRegion,ParameterValue=${this.targetRegion} \\
    ParameterKey=TemplateS3Bucket,ParameterValue=${this.targetTemplateBucket} \\
    ParameterKey=TemplateS3Key,ParameterValue=${this.targetTemplateKey}`
  }

  get updateStackSetsCommand(): string {
    const primaryComment = this.isTwoMgmtAccounts
      ? `# Update Primary Access StackSet (run from the Primary management account)`
      : `# Update Primary Access StackSet`
    const recoveryComment = this.isTwoMgmtAccounts
      ? `# Update Recovery Access StackSet (run from the Recovery management account)`
      : `# Update Recovery Access StackSet`
    return `${primaryComment}
aws cloudformation update-stack-set \\
  --stack-set-name ${this.primaryStackSetNameValue} \\
  --use-previous-template \\
  --administration-role-arn arn:aws:iam::${this.primaryMgmtAccountPlaceholder}:role/ArpioStackSetAdministrationRole \\
  --capabilities CAPABILITY_NAMED_IAM

${recoveryComment}
aws cloudformation update-stack-set \\
  --stack-set-name ${this.recoveryStackSetNameValue} \\
  --use-previous-template \\
  --administration-role-arn arn:aws:iam::${this.recoveryMgmtAccountPlaceholder}:role/ArpioStackSetAdministrationRole \\
  --capabilities CAPABILITY_NAMED_IAM`
  }

  // ==================== TROUBLESHOOTING COMMANDS ====================

  // Step 1 Troubleshooting
  get verifyAdminRoleCommand(): string {
    return `aws iam get-role --role-name ArpioStackSetAdministrationRole`
  }

  get verifyExecRoleCommand(): string {
    return `aws iam get-role --role-name ArpioStackSetExecutionRole`
  }

  get verifyExecRoleTrustCommand(): string {
    return `aws iam get-role --role-name ArpioStackSetExecutionRole \\
  --query 'Role.AssumeRolePolicyDocument'`
  }

  get checkAdminStackStatusCommand(): string {
    const adminComment = this.isTwoMgmtAccounts
      ? `# Check Administration Role stack (run from each management account)`
      : `# Check Administration Role stack (run from management account)`
    return `${adminComment}
aws cloudformation describe-stacks \\
  --stack-name ArpioStackSetAdministrationRole \\
  --query 'Stacks[0].{Status:StackStatus,Reason:StackStatusReason}'

# Check Execution Role stack (run from each member account)
aws cloudformation describe-stacks \\
  --stack-name ArpioStackSetExecutionRole \\
  --query 'Stacks[0].{Status:StackStatus,Reason:StackStatusReason}'`
  }

  get deleteAdminStackCommand(): string {
    const adminComment = this.isTwoMgmtAccounts
      ? `# Delete Administration Role stack (from each management account)`
      : `# Delete Administration Role stack (from management account)`
    return `${adminComment}
aws cloudformation delete-stack --stack-name ArpioStackSetAdministrationRole

# Delete Execution Role stack (from each member account)
aws cloudformation delete-stack --stack-name ArpioStackSetExecutionRole`
  }

  // Step 2 Troubleshooting
  get listStackSetsCommand(): string {
    return `aws cloudformation list-stack-sets --status ACTIVE`
  }

  get describeStackSetCommand(): string {
    if (!this.isTwoMgmtAccounts) {
      return `aws cloudformation describe-stack-set \\
  --stack-set-name ${this.primaryStackSetNameValue}`
    }
    return `# Describe Primary StackSet (run from Primary management account)
aws cloudformation describe-stack-set \\
  --stack-set-name ${this.primaryStackSetNameValue}

# Describe Recovery StackSet (run from Recovery management account)
aws cloudformation describe-stack-set \\
  --stack-set-name ${this.recoveryStackSetNameValue}`
  }

  get deleteStackSetCommand(): string {
    if (!this.isTwoMgmtAccounts) {
      return `# Only works if the StackSet has no stack instances
aws cloudformation delete-stack-set \\
  --stack-set-name ${this.primaryStackSetNameValue}`
    }
    return `# Delete Primary StackSet (run from Primary management account — only works if it has no stack instances)
aws cloudformation delete-stack-set \\
  --stack-set-name ${this.primaryStackSetNameValue}

# Delete Recovery StackSet (run from Recovery management account)
aws cloudformation delete-stack-set \\
  --stack-set-name ${this.recoveryStackSetNameValue}`
  }

  // Step 3 Troubleshooting
  get listStackInstancesCommand(): string {
    const primaryHeader = this.isTwoMgmtAccounts
      ? `# List Primary StackSet instances (run from Primary management account)`
      : `# List Primary StackSet instances`
    const recoveryHeader = this.isTwoMgmtAccounts
      ? `# List Recovery StackSet instances (run from Recovery management account)`
      : `# List Recovery StackSet instances`
    return `${primaryHeader}
aws cloudformation list-stack-instances \\
  --stack-set-name ${this.primaryStackSetNameValue}

${recoveryHeader}
aws cloudformation list-stack-instances \\
  --stack-set-name ${this.recoveryStackSetNameValue}`
  }

  get describeStackInstanceCommand(): string {
    const primaryHeader = this.isTwoMgmtAccounts
      ? `# Get detailed status of Primary stack instance (run from Primary management account)`
      : `# Get detailed status of Primary stack instance`
    const recoveryHeader = this.isTwoMgmtAccounts
      ? `# Get detailed status of Recovery stack instance (run from Recovery management account)`
      : `# Get detailed status of Recovery stack instance`
    return `${primaryHeader}
aws cloudformation describe-stack-instance \\
  --stack-set-name ${this.primaryStackSetNameValue} \\
  --stack-instance-account ${this.sourceAccountId} \\
  --stack-instance-region ${this.sourceRegion}

${recoveryHeader}
aws cloudformation describe-stack-instance \\
  --stack-set-name ${this.recoveryStackSetNameValue} \\
  --stack-instance-account ${this.targetAccountId} \\
  --stack-instance-region ${this.targetRegion}`
  }

  get listStackSetOperationsCommand(): string {
    const primaryHeader = this.isTwoMgmtAccounts
      ? `# List Primary StackSet operations (run from Primary management account)\n`
      : ''
    const recoveryHeader = this.isTwoMgmtAccounts
      ? `# List Recovery StackSet operations (run from Recovery management account)\n`
      : ''
    if (this.isTwoMgmtAccounts) {
      return `${primaryHeader}aws cloudformation list-stack-set-operations \\
  --stack-set-name ${this.primaryStackSetNameValue} \\
  --max-results 5

${recoveryHeader}aws cloudformation list-stack-set-operations \\
  --stack-set-name ${this.recoveryStackSetNameValue} \\
  --max-results 5`
    }
    return `aws cloudformation list-stack-set-operations \\
  --stack-set-name ${this.primaryStackSetNameValue} \\
  --max-results 5`
  }

  get retryPrimaryInstanceCommand(): string {
    const header = this.isTwoMgmtAccounts ? `# Run from Primary management account\n` : ''
    return `${header}aws cloudformation update-stack-instances \\
  --stack-set-name ${this.primaryStackSetNameValue} \\
  --accounts ${this.sourceAccountId} \\
  --regions ${this.sourceRegion}`
  }

  get retryRecoveryInstanceCommand(): string {
    const header = this.isTwoMgmtAccounts ? `# Run from Recovery management account\n` : ''
    return `${header}aws cloudformation update-stack-instances \\
  --stack-set-name ${this.recoveryStackSetNameValue} \\
  --accounts ${this.targetAccountId} \\
  --regions ${this.targetRegion}`
  }

  get deleteStackInstanceCommand(): string {
    const primaryHeader = this.isTwoMgmtAccounts
      ? `# Delete Primary stack instance (run from Primary management account)`
      : `# Delete Primary stack instance`
    const recoveryHeader = this.isTwoMgmtAccounts
      ? `# Delete Recovery stack instance (run from Recovery management account)`
      : `# Delete Recovery stack instance`
    return `${primaryHeader}
aws cloudformation delete-stack-instances \\
  --stack-set-name ${this.primaryStackSetNameValue} \\
  --accounts ${this.sourceAccountId} \\
  --regions ${this.sourceRegion} \\
  --no-retain-stacks

${recoveryHeader}
aws cloudformation delete-stack-instances \\
  --stack-set-name ${this.recoveryStackSetNameValue} \\
  --accounts ${this.targetAccountId} \\
  --regions ${this.targetRegion} \\
  --no-retain-stacks`
  }

  // Stack conflict troubleshooting commands
  get deleteSourceConflictStackCommand(): string {
    const sourceStackCmd = `aws cloudformation delete-stack --stack-name ${this.sourceStackName || 'arpio-source-access-YOUR_APP_NAME'} --region ${this.sourceRegion}`
    const targetStackCmd = `aws cloudformation delete-stack --stack-name ${this.targetStackName || 'arpio-target-access-YOUR_APP_NAME'} --region ${this.targetRegion}`
    return `# If conflict is in primary member account (${this.sourceAccountId}):
${sourceStackCmd}

# If conflict is in recovery member account (${this.targetAccountId}):
${targetStackCmd}`
  }

  get waitForStackDeleteCommand(): string {
    return `# Check stack status (should show DELETE_IN_PROGRESS, then disappear)
aws cloudformation describe-stacks --stack-name ${this.sourceStackName || 'arpio-source-access-YOUR_APP_NAME'} --query 'Stacks[0].StackStatus'`
  }

  // Scroll to a step section by name (prerequisites, create, deploy).
  // Delay must outlast the 200ms slideDown animation; otherwise getBoundingClientRect
  // captures a mid-animation position and the final scroll lands wrong. Offset uses
  // the live sticky-header height so the section's top clears any pinned header above.
  scrollToStep(stepName: string, openTroubleshooting: boolean = false): void {
    // Funnel through setSectionExpanded so deploy-step polling kicks in when
    // links like "return to Step 4" expand the section.
    this.setSectionExpanded(stepName, true)

    const scrollOffset = Math.max(this.stickyOffset + 16, 16)

    setTimeout(() => {
      const stepSections = document.querySelectorAll('.step-section')
      let targetSection: HTMLElement | null = null

      // Map step names to their index (0-based). Configuration is index 0; the
      // action steps shift down by one.
      const stepIndex = { config: 0, prerequisites: 1, create: 2, deploy: 3 }[stepName]
      if (stepIndex !== undefined && stepSections[stepIndex]) {
        targetSection = stepSections[stepIndex] as HTMLElement
      }

      if (targetSection) {
        // If opening troubleshooting, scroll to the troubleshooting section instead
        if (openTroubleshooting) {
          const troubleshootDetails = targetSection.querySelector(
            'details.troubleshooting-section'
          ) as HTMLDetailsElement
          if (troubleshootDetails) {
            if (!troubleshootDetails.open) {
              troubleshootDetails.open = true
            }
            setTimeout(() => {
              const rect = troubleshootDetails.getBoundingClientRect()
              const absoluteTop = rect.top + window.scrollY
              window.scrollTo({
                top: absoluteTop - scrollOffset,
                behavior: 'smooth',
              })
            }, 250)
          }
        } else {
          const rect = targetSection.getBoundingClientRect()
          const absoluteTop = rect.top + window.scrollY

          window.scrollTo({
            top: absoluteTop - scrollOffset,
            behavior: 'smooth',
          })
        }
      }
    }, 250)
  }

  // Scroll to a troubleshooting section by ID
  scrollToTroubleshooting(sectionId: string): void {
    const element = document.getElementById(sectionId)
    if (!element) return

    // Collect all ancestor details elements (outermost first)
    const detailsToOpen: HTMLDetailsElement[] = []
    let current: HTMLElement | null = element

    // If the element itself is a details, include it
    if (element.tagName === 'DETAILS') {
      detailsToOpen.push(element as HTMLDetailsElement)
    }

    // Walk up the DOM tree to find all parent details elements
    while (current) {
      const parentDetails = current.parentElement?.closest('details') as HTMLDetailsElement | null
      if (parentDetails) {
        detailsToOpen.unshift(parentDetails) // Add to beginning (outermost first)
        current = parentDetails
      } else {
        break
      }
    }

    // Open all collapsed details elements with staggered timing
    let delay = 0
    for (const details of detailsToOpen) {
      if (!details.open) {
        setTimeout(() => {
          details.open = true
        }, delay)
        delay += 150 // Stagger each expansion
      }
    }

    // Wait for all expansions to complete before scrolling
    setTimeout(() => {
      this.scrollAndHighlight(element)
    }, delay + 150)
  }

  // Scroll to element with offset and add temporary highlight
  private scrollAndHighlight(element: HTMLElement): void {
    // Calculate position with offset to show section header clearly
    const elementRect = element.getBoundingClientRect()
    const absoluteTop = elementRect.top + window.scrollY
    const offset = 200 // Offset to account for sticky headers and provide context

    window.scrollTo({
      top: absoluteTop - offset,
      behavior: 'smooth',
    })

    // Add highlight class after scroll starts
    setTimeout(() => {
      element.classList.add('highlight-pulse')
    }, 400)

    // Remove highlight class after animation completes (longer duration)
    setTimeout(() => {
      element.classList.remove('highlight-pulse')
    }, 4000)
  }
}
